/**
 * Resolves an asset path with the correct base URL for branch-based deployments.
 *
 * When the app is deployed to a branch-specific subdirectory (e.g., /my-branch/),
 * this function ensures assets are loaded from the correct path.
 *
 * @param path - The relative path to the asset (e.g., "iconWhite.svg" or "icons/slack.png")
 * @returns The full path including the base URL (e.g., "/my-branch/iconWhite.svg" or "/iconWhite.svg")
 *
 * @example
 * ```tsx
 * <img src={getAssetPath("iconWhite.svg")} alt="Logo" />
 * <img src={getAssetPath("icons/slack.png")} alt="Slack" />
 * ```
 */
export function getAssetPath(path: string): string {
  // Remove leading slash if present to avoid double slashes
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  // VITE_BRANCH_NAME is set during build for branch-based deployments
  const branchName = import.meta.env.VITE_BRANCH_NAME as string | undefined;

  if (branchName) {
    return `/${branchName}/${normalizedPath}`;
  }

  return `/${normalizedPath}`;
}
